import {
  ENABLE_SERVICE_FEE,
  GROUP_STAMP_DUTY,
  PLAN_BILL_CYCLE,
  SERVICE_FEE_EFFECTIVE_DATE,
  SERVICE_FEE_MONTHLY_PERCENTAGE,
  SERVICE_FEE_REMOVAL_DATE,
  SERVICE_FEE_YEARLY_PERCENTAGE,
} from '@utils/constant';

/**
 * Check if service fee should be applied based on the given date
 * Service fee is effective if:
 * - ENABLE_SERVICE_FEE is true
 * - date >= SERVICE_FEE_EFFECTIVE_DATE
 * - date < SERVICE_FEE_REMOVAL_DATE
 *
 * @param date The date to check (defaults to current date)
 * @returns true if service fee should be applied
 */
export const isServiceFeeEffective = (date: Date = new Date()): boolean => {
  if (!ENABLE_SERVICE_FEE) {
    return false;
  }
  const effectiveDate = new Date(SERVICE_FEE_EFFECTIVE_DATE);
  const removalDate = new Date(SERVICE_FEE_REMOVAL_DATE);
  return date >= effectiveDate && date < removalDate;
};

/**
 * Calculate the service fee based on the premium, tax, and billing cycle
 *
 * @param premium     The premium amount
 * @param tax         The tax amount
 * @param billCycle   The billing cycle (monthly or yearly)
 * @param withStampDuty Whether to include stamp duty in the calculation
 * @param cents       Whether the amounts are in cents
 * @param checkDate   Optional date to check if service fee should be applied (defaults to current date)
 *
 * @returns
 */
export const calculateServiceFee = (
  premium: number,
  tax: number,
  billCycle: string,
  withStampDuty = false,
  cents = false,
  checkDate: Date | null = null
) => {
  if (premium == 0 || tax == 0 || !ENABLE_SERVICE_FEE) {
    return 0;
  }

  // Check if service fee should be applied based on the date
  const dateToCheck = checkDate || new Date();
  if (!isServiceFeeEffective(dateToCheck)) {
    return 0;
  }

  const format = cents ? 1 : 100;

  const serviceFeePercentage =
    billCycle === PLAN_BILL_CYCLE.YEARLY
      ? SERVICE_FEE_YEARLY_PERCENTAGE
      : SERVICE_FEE_MONTHLY_PERCENTAGE;

  const payableAmount =
    premium + tax + (withStampDuty ? GROUP_STAMP_DUTY / format : 0);

  return payableAmount * serviceFeePercentage;
};
